import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowsClockwise,
  Image,
  StarFour,
  UploadSimple,
} from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { ImageService } from '../../../service/ImageService';
import { UploadService } from '../../../service/UploadService';
import { useAuthStore } from '../../../store/storeAuth';
import KitModal from '../KitModal';
import { ImageSource, ImageSourceModalProps } from './types';

const ImageSourceModal: React.FC<ImageSourceModalProps> = ({
  show,
  onHide,
  onImageSelected,
}) => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';

  const [sourceUsed, setSourceUsed] = useState<ImageSource | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateStep, setGenerateStep] = useState<'input' | 'result'>('input');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!show) {
      setSourceUsed(null);
      setImageUrl('');
      setUploadedFile(null);
      setIsUploading(false);
      setPreviewUrl('');
      setError(null);
      setShowGenerateModal(false);
      setGenerateStep('input');
      setGeneratePrompt('');
      setGeneratedImageUrl(null);
      setIsGenerating(false);
    }
  }, [show]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, or GIF files.');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setError(null);
    setUploadedFile(file);
    setSourceUsed('upload');

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await UploadService.uploadFile(formData);
      setImageUrl(response.data.src);
      setError(null);
    } catch (err) {
      const errorMsg = getAPIErrorMessage(err as any);
      setError(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const openGenerateModal = () => {
    setError(null);
    setShowGenerateModal(true);
    setGenerateStep('input');
    setGeneratePrompt('');
    setGeneratedImageUrl(null);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setGenerateStep('input');
    setGeneratePrompt('');
    setGeneratedImageUrl(null);
  };

  const handleGenerateSubmit = async () => {
    if (!generatePrompt.trim()) return;
    setIsGenerating(true);
    setError(null);
    try {
      const response = await ImageService.addImageWithAi({
        description: generatePrompt.trim(),
      });
      const url = response.data.message;
      setGeneratedImageUrl(url);
      setImageUrl(url);
      setPreviewUrl(url);
      setSourceUsed('generate');
      setGenerateStep('result');
      setError(null);
    } catch (err) {
      setError(getAPIErrorMessage(err as any));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = () => {
    setGenerateStep('input');
    setGeneratedImageUrl(null);
    setError(null);
  };

  const handleUseGenerated = () => {
    closeGenerateModal();
  };

  const handleContinue = () => {
    if (!imageUrl) {
      setError('No image selected. Please upload or generate an image first.');
      return;
    }
    onImageSelected(imageUrl, sourceUsed ?? 'upload');
    onHide();
  };

  const outlineButtonSx = {
    borderColor: '#4262E0',
    color: '#4262E0',
    bgcolor: isDark ? '#1D2939' : '#EEF1FE',
    maxWidth: 280,
    alignSelf: 'center',
    '&:hover': {
      borderColor: '#3557C7',
      bgcolor: isDark ? '#344054' : '#DDE3FC',
    },
  };

  const placeholderBoxSx = {
    borderRadius: '8px',
    border: `2px dashed ${isDark ? '#475467' : '#D0D5DD'}`,
    mt: 1.5,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    height: '160px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    bgcolor: isDark ? '#1D2939' : '#F9FAFB',
  };

  const addImageFooter = (
    <Box sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
      <Button
        variant="contained"
        onClick={handleContinue}
        disabled={!imageUrl || isUploading}
        fullWidth
        sx={{
          bgcolor: '#4262E0',
          '&:hover': { bgcolor: '#3557C7' },
        }}
      >
        Continue
      </Button>
    </Box>
  );

  const generateModalFooter =
    generateStep === 'input' ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          p: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={closeGenerateModal}
          sx={{
            color: '#4262E0',
            borderColor: '#4262E0',
            bgcolor: isDark ? '#1D2939' : '#FFFFFF',
            '&:hover': {
              borderColor: '#3557C7',
              bgcolor: isDark ? '#1E3A5F' : '#EEF1FE',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerateSubmit}
          disabled={!generatePrompt.trim() || isGenerating}
          endIcon={
            isGenerating ? (
              <CircularProgress size={18} sx={{ color: '#FFF' }} />
            ) : (
              <StarFour
                size={20}
                color={!generatePrompt.trim() ? '#9CA3AF' : '#FFF'}
                weight="fill"
              />
            )
          }
          sx={{
            bgcolor: '#4262E0',
            '&:hover': { bgcolor: '#3557C7' },
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate'}
        </Button>
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          p: 2,
        }}
      >
        <Button
          variant="outlined"
          endIcon={<ArrowsClockwise size={20} />}
          onClick={handleRegenerate}
          sx={{
            color: '#4262E0',
            borderColor: '#4262E0',
            bgcolor: isDark ? '#1D2939' : '#FFFFFF',
            '&:hover': {
              borderColor: '#3557C7',
              bgcolor: isDark ? '#1E3A5F' : '#EEF1FE',
            },
          }}
        >
          Regenerate
        </Button>
        <Button
          variant="contained"
          onClick={handleUseGenerated}
          sx={{
            bgcolor: '#4262E0',
            '&:hover': { bgcolor: '#3557C7' },
          }}
        >
          Use
        </Button>
      </Box>
    );

  const titleIconBoxSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '2px',
    bgcolor: isDark ? '#344054' : '#EEF1FE',
  };

  const modalTitle = showGenerateModal ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={titleIconBoxSx}>
        <StarFour
          size={24}
          color={isDark ? '#D0D5DD' : '#4262E0'}
          weight="fill"
        />
      </Box>
      <span style={{ fontWeight: 500, fontSize: '18px', color: isDark ? '#D0D5DD' : '#101828' }}>
        Generate Image with AI
      </span>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span style={{ fontWeight: 500, fontSize: '18px', color: isDark ? '#D0D5DD' : '#101828' }}>
        Add an image
      </span>
    </Box>
  );

  const modalContent = showGenerateModal ? (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: isDark ? '#475467' : '#EAECF0',
          mx: -3,
          mt: -5,
          mb: 3,
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {generateStep === 'input' ? (
        <Box sx={{ width: '100%', mb: -4 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Describe your Image"
            multiline
            minRows={4}
            placeholder="e.g a sunset with two people drinking a soda"
            value={generatePrompt}
            onChange={(e) => setGeneratePrompt(e.target.value)}
            disabled={isGenerating}
            sx={{
              bgcolor: isDark ? '#1D2939' : '#F9FAFB',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: isDark ? '#475467' : '#EAECF0' },
              },
              '& .MuiInputLabel-outlined': {
                color: isDark ? '#98A2B3' : '#667085',
              },
            }}
          />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: `1px solid ${isDark ? '#475467' : '#EAECF0'}`,
              backgroundImage: generatedImageUrl
                ? `url("${generatedImageUrl}")`
                : 'none',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              minHeight: 280,
              bgcolor: isDark ? '#1D2939' : '#F9FAFB',
            }}
          />
        </>
      )}
    </Box>
  ) : (
    <Box sx={{ p: 3 }}>
      <Typography
        sx={{
          fontSize: '14px',
          color: isDark ? '#98A2B3' : '#667085',
          mb: 3,
        }}
      >
        You can upload your own image or choose to generate one using our AI
        power.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <FormLabel htmlFor="image-upload" sx={{ display: 'block', cursor: 'pointer' }}>
          <Button
            variant="outlined"
            component="span"
            endIcon={<UploadSimple size={20} />}
            sx={outlineButtonSx}
          >
            Upload Image
          </Button>
        </FormLabel>
        <Typography
          sx={{
            color: isDark ? '#98A2B3' : '#667085',
            fontSize: '14px',
            flexShrink: 0,
          }}
        >
          or
        </Typography>
        <Button
          variant="outlined"
          onClick={openGenerateModal}
          endIcon={<StarFour size={20} weight="fill" />}
          sx={outlineButtonSx}
        >
          Generate Image
        </Button>
        <input
          id="image-upload"
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      <Box
        sx={{
          ...placeholderBoxSx,
          width: '100%',
          backgroundImage: previewUrl ? `url("${previewUrl}")` : 'none',
          position: 'relative',
        }}
      >
        {!previewUrl && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Image size={24} color={isDark ? '#98A2B3' : '#667085'} />
            <Typography
              sx={{
                fontSize: '14px',
                color: isDark ? '#98A2B3' : '#667085',
              }}
            >
              Your image will appear here
            </Typography>
          </Box>
        )}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: '8px',
            }}
          >
            <CircularProgress sx={{ color: '#4262E0' }} />
          </Box>
        )}
      </Box>
    </Box>
  );

  const handleModalClose = () => {
    if (showGenerateModal) {
      closeGenerateModal();
    } else {
      onHide();
    }
  };

  return (
    <KitModal
      show={show}
      onHide={handleModalClose}
      title={modalTitle}
      footer={showGenerateModal ? generateModalFooter : addImageFooter}
      maxWidth="sm"
      fullWidth
    >
      {modalContent}
    </KitModal>
  );
};

export default ImageSourceModal;
